import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import DownloadIcon from '@mui/icons-material/GetApp';
import {
  fetchRelatedRecords,
  useDataProvider,
  useNotify,
  useListContext,
  SortPayload,
  Exporter,
  FilterPayload,
  useResourceContext,
} from 'ra-core';
import { Button, ButtonProps } from '@mui/material';

import { ColumnType, useDefaultExporter } from './use-default-exporter';

const EXPORT_PAGE_SIZE = 100;
const MAX_EXPORT_PAGES = 10000;

export const CustomExportButton = (props: ExportButtonProps & CustomExportProps) => {
  const {
    maxResults = 1000,
    onClick,
    label = 'ra.action.export',
    icon = defaultIcon,
    exporter: customExporter,
    columns,
    meta,
    onExport,
    ...rest
  } = props;
  const {
    filter,
    filterValues,
    sort,
    total,
    selectedIds,
  } = useListContext(props);
  const resource = useResourceContext(props);
  const defaultExporter = useDefaultExporter(columns);
  const exporter = customExporter || defaultExporter;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [isExporting, setExporting] = useState(false);
  const isExportingRef = useRef(false);

  const transformQueryParams = (queryParams: Record<string, any>, selectedIds: number[]) => {
    const params = {
      _start: 0,
      _end: maxResults,
      _sort: queryParams.sort?.field,
      _order: queryParams.sort?.order,
    };
    if (Array.isArray(selectedIds) && selectedIds.length > 0) {
      params['id'] = selectedIds;
    }

    for (const [field, value] of Object.entries(queryParams.filter || {})) {
      params[field] = value;
    }

    return params;
  };

  const handleClick = useCallback(
    event => {
      if (isExportingRef.current) {
        return;
      }
      isExportingRef.current = true;

      const listFilter = {
        ...(filter ? { ...filterValues, ...filter } : filterValues),
        ...(selectedIds.length > 0 ? { id: selectedIds } : {}),
      };
      const queryParams = {
        sort,
        filter: listFilter,
        pagination: { page: 1, perPage: EXPORT_PAGE_SIZE },
        meta,
      };

      if (onExport) {
        onExport(transformQueryParams(queryParams, selectedIds));
      }

      setExporting(true);
      const loadPages = async () => {
        const rows: any[] = [];
        const seenIds = new Set<string>();
        let page = 1;

        while (page <= MAX_EXPORT_PAGES && rows.length < maxResults) {
          const perPage = Math.min(EXPORT_PAGE_SIZE, maxResults - rows.length);
          const { data } = await dataProvider.getList(resource, {
            sort,
            filter: listFilter,
            pagination: { page, perPage },
            meta,
          });

          let added = 0;
          for (const row of data) {
            const key = String(row.id);
            if (seenIds.has(key)) {
              continue;
            }
            seenIds.add(key);
            rows.push(row);
            added += 1;
          }

          if (added === 0 || data.length < perPage) {
            break;
          }
          page += 1;
        }

        return rows;
      };

      loadPages()
        .then(data => exporter && exporter(
          data,
          fetchRelatedRecords(dataProvider),
          dataProvider,
          resource,
        ))
        .catch(error => {
          console.error(error);
          notify('ra.notification.http_error', { type: 'error' });
        })
        .finally(() => {
          isExportingRef.current = false;
          setExporting(false);
        });
      if (typeof onClick === 'function') {
        onClick(event);
      }
    },
    [
      dataProvider,
      exporter,
      filter,
      filterValues,
      maxResults,
      onExport,
      notify,
      onClick,
      resource,
      sort,
      meta,
      selectedIds,
    ],
  );

  return (
    <Button
      onClick={handleClick}
      {...sanitizeRestProps(rest) as any}
      disabled={Boolean(rest.disabled) || total === 0 || isExporting}
      className="MuiButtonBase-root"
      aria-label="Export"
      size="small"
    >
      {icon} {isExporting ? 'Exporting...' : 'Export'}
    </Button>
  );
};

const defaultIcon = <DownloadIcon />;

const sanitizeRestProps = ({
  filterValues,
  resource,
  ...rest
}: Omit<
  ExportButtonProps,
  'sort' | 'maxResults' | 'label' | 'exporter' | 'meta'
>) => rest;

interface Props {
  exporter?: Exporter;
  filterValues?: FilterPayload;
  icon?: JSX.Element;
  label?: string;
  maxResults?: number;
  onClick?: (e: Event) => void;
  resource?: string;
  sort?: SortPayload;
  meta?: any;
}

interface CustomExportProps {
  columns: ColumnType[];
  onExport?: (queryParams: any) => Promise<void>;
}

export type ExportButtonProps = Props & ButtonProps;
